import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

// ---------------------------------------------------------------------------
// AFE structuring layout state. Mirrors
// `pages/documents/structuring/atoms/document-layout.ts` — left section-nav
// width + show/hide flags persisted to sessionStorage, plus the active section
// and the currently-selected well index (Match Review drill-in).
// ---------------------------------------------------------------------------

/** The four structuring sections. Only `wells` is functional in Story 1. */
export type StructuringSection =
  'identification' | 'wells' | 'costs' | 'related_documents';

export interface AfeStructuringLayoutState {
  showLeftPanel: boolean;
  showDocViewer: boolean;
}

export const LEFT_PANEL_MIN_WIDTH_PX = 180;
export const LEFT_PANEL_MAX_WIDTH_PX = 480;
export const LEFT_PANEL_DEFAULT_WIDTH_PX = 320;

const STORAGE_KEY = 'afe-structuring-layout';
const DEFAULT: AfeStructuringLayoutState = {
  showLeftPanel: true,
  showDocViewer: true,
};

const storage = createJSONStorage<AfeStructuringLayoutState>(
  () => sessionStorage
);

export const afeStructuringLayoutAtom =
  atomWithStorage<AfeStructuringLayoutState>(STORAGE_KEY, DEFAULT, storage);

export const toggleLeftPanelAtom = atom(null, (get, set) => {
  const current = get(afeStructuringLayoutAtom);
  set(afeStructuringLayoutAtom, {
    ...current,
    showLeftPanel: !current.showLeftPanel,
  });
});

export const toggleDocViewerAtom = atom(null, (get, set) => {
  const current = get(afeStructuringLayoutAtom);
  set(afeStructuringLayoutAtom, {
    ...current,
    showDocViewer: !current.showDocViewer,
  });
});

export const leftPanelWidthAtom = atom<number>(LEFT_PANEL_DEFAULT_WIDTH_PX);

/** Active section in the left nav. */
export const activeSectionAtom = atom<StructuringSection>('wells');

/**
 * Index of the well being reviewed in Match Review (null = Well Overview).
 * Mirrors `selectedPartyIndexAtom` from structured-parties.
 */
export const selectedWellIndexAtom = atom<number | null>(null);

/**
 * afeHeaderId of the well being drilled into on the Cost section (null = Cost
 * Overview). Keyed by afeHeaderId because Structure Costs is `GET .../costs/:wellId`
 * where wellId = afeHeaderId. Mirrors `selectedWellIndexAtom` for the Cost tab.
 */
export const selectedCostWellIdAtom = atom<number | null>(null);
